import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSeminar, putSeminar, getMentor, putMentor, formatApiError } from "@/lib/api";
import { Loader2, RefreshCw, Save, Calendar, Sparkles, Phone, User, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function Field({ label, hint, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
        </div>
    );
}

function T({ label, value, onChange, testid, hint, type = "text", placeholder = "" }) {
    return (
        <Field label={label} hint={hint}>
            <Input
                data-testid={testid}
                type={type}
                value={value ?? ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="h-11 rounded-xl bg-white"
            />
        </Field>
    );
}

function TA({ label, value, onChange, testid, rows = 3, hint }) {
    return (
        <Field label={label} hint={hint}>
            <Textarea
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="rounded-xl bg-white"
            />
        </Field>
    );
}

function Section({ icon: Icon, title, description, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                    {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
                </div>
            </div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

export default function SeminarDetailsTab() {
    const [seminar, setSeminar] = useState(null);
    const [mentor, setMentor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, m] = await Promise.all([getSeminar(), getMentor()]);
            setSeminar(s); setMentor(m);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setS = (k) => (v) => setSeminar({ ...seminar, [k]: v });
    const setM = (k) => (v) => setMentor({ ...mentor, [k]: v });

    const save = async () => {
        setSaving(true);
        try {
            const [s, m] = await Promise.all([putSeminar(seminar), putMentor(mentor)]);
            setSeminar(s); setMentor(m);
            toast.success("Seminar details updated on live landing page ✅");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (loading || !seminar || !mentor) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
        );
    }

    // Benefits stored as list — edit as multi-line textarea (one per line)
    const benefitsText = (seminar.benefits || []).join("\n");
    const setBenefits = (v) => setSeminar({ ...seminar, benefits: v.split("\n").map((x) => x.trim()).filter(Boolean) });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 sticky top-14 bg-slate-50 z-10 py-3 -mx-6 px-6 border-b border-slate-200">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Seminar Details</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Landing page वरील सर्व text, prices, contact आणि mentor माहिती एका ठिकाणी edit करा.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm px-3 py-2" data-testid="seminar-reload">
                        <RefreshCw className="w-4 h-4" /> Reload
                    </button>
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 disabled:opacity-70" data-testid="seminar-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
                    </button>
                </div>
            </div>

            <Section icon={Sparkles} title="Hero Section" description="Landing page वर सर्वात वर दिसणारा मुख्य content.">
                <TA label="Hero Title (Marathi)" value={seminar.hero_title} onChange={setS("hero_title")} testid="f-hero-title" rows={2}
                    hint="Line 1 navy मध्ये, Line 2 orange मध्ये दिसते. दोन ओळींसाठी Enter दाबा." />
                <TA label="Hero Subtitle / Description" value={seminar.hero_subtitle} onChange={setS("hero_subtitle")} testid="f-hero-sub" rows={2} />
                <T label="CTA Button Text" value={seminar.hero_cta} onChange={setS("hero_cta")} testid="f-hero-cta" placeholder="आता नोंदणी करा" />
                <TA label="Benefits (each on new line)" value={benefitsText} onChange={setBenefits} testid="f-benefits" rows={4}
                    hint="Hero खाली green ✓ marks सह दिसतात." />
            </Section>

            <Section icon={CreditCard} title="Fees & Payment">
                <div className="grid md:grid-cols-2 gap-4">
                    <T label="Registration Fee (₹)" value={seminar.registration_fee} onChange={setS("registration_fee")} testid="f-fee" placeholder="199" />
                    <T label="Original / Offer Price (strike-through)" value={seminar.original_fee} onChange={setS("original_fee")} testid="f-fee-orig" placeholder="1999" />
                </div>
                <T label="Razorpay Payment Page URL (optional backup)" value={seminar.razorpay_payment_url} onChange={setS("razorpay_payment_url")} testid="f-rzp-url"
                    placeholder="https://rzp.io/rzp/xxxxxx" />
            </Section>

            <Section icon={Calendar} title="Seminar Date, Time & Venue">
                <div className="grid md:grid-cols-2 gap-4">
                    <T label="Date" value={seminar.date_text} onChange={setS("date_text")} testid="f-date" placeholder="रविवार" />
                    <T label="Time" value={seminar.time_text} onChange={setS("time_text")} testid="f-time" placeholder="सकाळी 11:00" />
                </div>
                <T label="Venue / Location" value={seminar.venue} onChange={setS("venue")} testid="f-venue" placeholder="Mumbai, Maharashtra" />
                <T label="Google Maps URL (optional)" value={seminar.google_map_url} onChange={setS("google_map_url")} testid="f-map"
                    placeholder="https://maps.google.com/?q=..." />
            </Section>

            <Section icon={Sparkles} title="Urgency Section" description="Page मध्ये dark navy banner वर दिसणारा text.">
                <T label="Heading" value={seminar.urgency_heading} onChange={setS("urgency_heading")} testid="f-urg-h" />
                <TA label="Description" value={seminar.urgency_description} onChange={setS("urgency_description")} testid="f-urg-d" rows={2} />
                <T label="Urgency CTA Text" value={seminar.urgency_cta} onChange={setS("urgency_cta")} testid="f-urg-cta" placeholder="Seat Reserve करा" />
            </Section>

            <Section icon={User} title="Mentor Information">
                <div className="grid md:grid-cols-2 gap-4">
                    <T label="Mentor Name" value={mentor.name} onChange={setM("name")} testid="m-name" />
                    <T label="Designation" value={mentor.designation} onChange={setM("designation")} testid="m-desig" />
                    <T label="Years of Experience" value={mentor.experience} onChange={setM("experience")} testid="m-exp" placeholder="10+" />
                    <T label="Businesses Guided" value={mentor.businesses_guided} onChange={setM("businesses_guided")} testid="m-biz" placeholder="500+" />
                </div>
                <T label="Mentor Photo URL" value={mentor.primary_image} onChange={setM("primary_image")} testid="m-img"
                    placeholder="https://..." hint="Photo चा direct link. रिकामे ठेवल्यास default photo दिसेल." />
                <TA label="Mission Statement (short quote)" value={mentor.mission_statement} onChange={setM("mission_statement")} testid="m-mission" rows={2} />
                <TA label="About / Description" value={mentor.about} onChange={setM("about")} testid="m-about" rows={4} />
                <T label="CTA Button Text" value={mentor.cta_text} onChange={setM("cta_text")} testid="m-cta" placeholder="Book Your Seat" />
            </Section>

            <Section icon={Phone} title="Contact / WhatsApp Details">
                <div className="grid md:grid-cols-2 gap-4">
                    <T label="Contact Name" value={seminar.contact_name} onChange={setS("contact_name")} testid="f-c-name" />
                    <T label="Contact Title / Role" value={seminar.contact_title} onChange={setS("contact_title")} testid="f-c-title" />
                    <T label="Mobile Number" value={seminar.contact_phone} onChange={setS("contact_phone")} testid="f-c-phone" placeholder="+91 XXXXXXXXXX" />
                    <T label="WhatsApp Number" value={seminar.contact_whatsapp} onChange={setS("contact_whatsapp")} testid="f-c-wa" placeholder="+919XXXXXXXXX" />
                </div>
                <T label="Email" value={seminar.contact_email} onChange={setS("contact_email")} testid="f-c-email" type="email" />
            </Section>
        </div>
    );
}
